import { apiClient } from '@/api/client';
import type { ApiResponse, GenerateRoutineParams, Routine, Workout } from '@/api/types';
import {
  equipmentOptions,
  exercises,
  levels,
  sportCategories,
  sports,
} from '@/components/training/exerciseData';

const USE_MOCKED_DATA = true;

const mockApiDelay = (ms: number = 500) => new Promise((resolve) => setTimeout(resolve, ms));

const generateMockedRoutine = (params: GenerateRoutineParams): Routine => {
  const { sport, days, level, equipment } = params;

  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const selectedDays = daysOfWeek.slice(0, days);

  const dayTypes = ['Full Body', 'Upper Body', 'Lower Body', 'Cardio', 'Sport Specific'];
  const dayFocuses = [
    'Strength & Power',
    'Endurance & Stamina',
    'Hypertrophy & Muscle',
    'Functional Movement',
    'Active Recovery',
  ];

  const workoutTemplates: Record<string, Workout> = {
    'Full Body': {
      day: '',
      type: 'Full Body',
      focus: 'Complete body workout',
      exercises: [
        ...exercises.push.slice(0, 2).map((ex) => ({ ...ex })),
        ...exercises.pull.slice(0, 2).map((ex) => ({ ...ex })),
        ...exercises.legs.slice(0, 2).map((ex) => ({ ...ex })),
        ...exercises.core.slice(0, 1).map((ex) => ({ ...ex })),
      ].filter((ex) =>
        ex.equipment.some(
          (eq) =>
            equipment.includes(eq) ||
            equipment.includes('full-gym') ||
            equipment.includes('dumbbells'),
        ),
      ),
    },
    'Upper Body': {
      day: '',
      type: 'Upper Body',
      focus: 'Push & Pull',
      exercises: [
        ...exercises.push.slice(0, 3).map((ex) => ({ ...ex })),
        ...exercises.pull.slice(0, 3).map((ex) => ({ ...ex })),
        ...exercises.core.slice(0, 1).map((ex) => ({ ...ex })),
      ].filter((ex) =>
        ex.equipment.some(
          (eq) =>
            equipment.includes(eq) ||
            equipment.includes('full-gym') ||
            equipment.includes('dumbbells'),
        ),
      ),
    },
    'Lower Body': {
      day: '',
      type: 'Lower Body',
      focus: 'Legs & Core',
      exercises: [
        ...exercises.legs.slice(0, 5).map((ex) => ({ ...ex })),
        ...exercises.core.slice(0, 2).map((ex) => ({ ...ex })),
      ].filter((ex) =>
        ex.equipment.some(
          (eq) =>
            equipment.includes(eq) ||
            equipment.includes('full-gym') ||
            equipment.includes('dumbbells'),
        ),
      ),
    },
    Cardio: {
      day: '',
      type: 'Cardio',
      focus: 'Cardiovascular Endurance',
      exercises: [...exercises.cardio.slice(0, 4).map((ex) => ({ ...ex }))].filter((ex) =>
        ex.equipment.some((eq) => equipment.includes(eq) || equipment.includes('bodyweight')),
      ),
    },
    'Sport Specific': {
      day: '',
      type: 'Sport Specific',
      focus: `${sport} Training`,
      exercises: [
        ...(exercises.sportDrills[sport as keyof typeof exercises.sportDrills]
          ?.slice(0, 4)
          .map((ex) => ({ ...ex })) || []),
        ...exercises.core.slice(0, 1).map((ex) => ({ ...ex })),
      ].filter((ex) =>
        ex.equipment.some((eq) => equipment.includes(eq) || equipment.includes('bodyweight')),
      ),
    },
  };

  const routine: Workout[] = selectedDays.map((day) => {
    const randomType = dayTypes[Math.floor(Math.random() * dayTypes.length)];
    const randomFocus = dayFocuses[Math.floor(Math.random() * dayFocuses.length)];

    let workout = workoutTemplates[randomType];

    if (workout.exercises.length === 0) {
      workout = workoutTemplates['Full Body'];
    }

    return {
      ...workout,
      day,
      focus: randomFocus,
    };
  });

  return {
    sport,
    category: sport,
    level,
    daysPerWeek: days,
    equipment,
    routine,
    generatedAt: new Date().toISOString(),
  };
};

export const fetchData = async (): Promise<ApiResponse> => {
  if (USE_MOCKED_DATA) {
    await mockApiDelay();
    return {
      exercises,
      sportCategories,
      sports,
      equipmentOptions,
      levels,
    };
  }

  return apiClient('/api/data') as Promise<ApiResponse>;
};

export const generateRoutine = async (params: GenerateRoutineParams): Promise<Routine> => {
  if (USE_MOCKED_DATA) {
    await mockApiDelay(800);
    return generateMockedRoutine(params);
  }

  return apiClient('/api/routines/generate', {
    method: 'POST',
    body: JSON.stringify(params),
  }) as Promise<Routine>;
};
